import { useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { KaboodleToaster } from "components/common";
import { ControlledTextArea, TextInput } from "components/inputs";
import { DatePicker, DATE_TO_STRING, STRING_TO_DATE } from "components/inputs";
import type { MSADataType } from "types/DataTypes";
import { throttle } from "lodash";
import FutureMedicalCard from "components/Card/FutureMedicalCard";

interface Props {
  service: MSADataType;
  onSave: (field: string, value: unknown) => Promise<void>;
}

interface FormData {
  amended_review_date_of_assignment: string | null;
  amended_review_attempt_number: number | null;
  amended_review_overview_explanation: string | null;
}

const AmendedReviewOverviewCard = ({ service, onSave }: Props) => {
  const {
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  useEffect(() => {
    reset({
      amended_review_date_of_assignment:
        service?.amended_review_date_of_assignment || null,
      amended_review_attempt_number:
        service?.amended_review_attempt_number || null,
      amended_review_overview_explanation:
        service?.amended_review_overview_explanation || null,
    });
  }, [service, reset]);

  const handleFieldSave = useCallback(
    throttle(async (field: string, value: unknown) => {
      try {
        await onSave(field, value);
      } catch (error) {
        KaboodleToaster.error(error);
      }
    }, 800),
    [onSave],
  );

  return (
    <FutureMedicalCard heading="Amended Review Overview" cardNumber={1}>
      <Controller
        name="amended_review_date_of_assignment"
        control={control}
        render={({ field }) => (
          <DatePicker
            {...field}
            label="Date of Assignment"
            errors={errors}
            onDateChange={(date) => {
              const formattedDate = DATE_TO_STRING(date);
              field.onChange(formattedDate);
              handleFieldSave(
                "amended_review_date_of_assignment",
                formattedDate,
              );
            }}
            initialDate={
              service?.amended_review_date_of_assignment
                ? STRING_TO_DATE(service.amended_review_date_of_assignment)
                : undefined
            }
          />
        )}
      />

      <Controller
        name="amended_review_attempt_number"
        control={control}
        render={({ field }) => (
          <TextInput
            {...field}
            type="number"
            label="Attempt"
            errors={errors}
            onChange={field.onChange}
            onBlur={() =>
              handleFieldSave("amended_review_attempt_number", field.value)
            }
          />
        )}
      />

      <ControlledTextArea
        name="amended_review_overview_explanation"
        control={control}
        errors={errors}
        label="Explanation"
        advancedEdit={true}
        onSave={(value) =>
          handleFieldSave("amended_review_overview_explanation", String(value))
        }
      />
    </FutureMedicalCard>
  );
};

export default AmendedReviewOverviewCard;
